import { memo, useCallback, useEffect, useRef, useLayoutEffect } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { createUseStyles } from 'react-jss'
import gsap from 'gsap'
import Modernizr from '@/vendors/modernizr'
import DelayLink from '@/components/DelayLink'
import UnderlinedText from '@/components/UnderlinedText'
// import Accordion from '@/components/Accordion'
import usePrevious from '@/hooks/usePrevious'
// import useMediaQuery from '@/hooks/useMediaQuery'
import { getSlug } from '@/utils/path'
import { decodeEntities } from '@/utils/decodeEntities'
import * as layerActions from '@/actions/layer'
import style from './style'

const useStyles = createUseStyles(style)

const Nav = () => {
  const $root = useRef()
  const $wrapper = useRef()
  const $mainNav = useRef()
  const $social = useRef()
  const location = useLocation()

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { pathname, isMenuOpen, mainNav, headerHeight, strings, isLanding, mainMenuLanding } = useSelector((state) => ({
    pathname: state.router.location.pathname,
    isMenuOpen: state.layer.layers.some((layer) => layer.id === 'menu' && layer.isOpen),
    extraNav: state.nav.extra_menu || [],
    isLanding: state.layout.isLanding,
    productNav: state.nav.product_menu || [],
    mainNav: state.nav.main_menu || [],
    strings: state.options.strings,
    headerHeight: state.bounds?.header?.height || 0,
    mainMenuLanding: state.content.pages.landing?.acf.main_menu.list_main || [],
  }), shallowEqual)

  const prevPathname = usePrevious(pathname)
  const classes = useStyles({ headerHeight, isMenuOpen })

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const closeMenu = useCallback(() => dispatch(layerActions.closeMenu()), [dispatch])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && e.keyCode === 27 && isMenuOpen) closeMenu()
  }, [isMenuOpen])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isMenuOpen])

  const openMainMenu = useCallback(() => {
    if (Modernizr.devicehastouch) $root.current.scrollTop = 0
    gsap.killTweensOf([$wrapper.current, $mainNav.current, $social.current])
    gsap.to($wrapper.current, {
      y: '0%',
      ease: 'power3.out',
      duration: 1,
    })
    gsap.to([$mainNav.current, $social.current], {
      opacity: 1,
      delay: 0.2,
      ease: 'power3.out',
      duration: 1,
      scaleY: 1,
    })
  }, [])

  const closeMainMenu = useCallback(() => {
    gsap.killTweensOf([$wrapper.current, $mainNav.current, $social.current])
    gsap.to($wrapper.current, {
      y: '-100%',
      ease: 'power3.out',
      duration: 1,
    })
    gsap.to([$mainNav.current, $social.current], {
      opacity: 0,
      ease: 'power3.out',
      duration: 1,
      scaleY: 0.7,
    })
  }, [])

  /*------------------------------
  Close Nav when isMenuOpen changed
  ------------------------------*/
  const prevIsOpen = usePrevious(isMenuOpen)
  useEffect(() => {
    if (prevIsOpen !== undefined && isMenuOpen) openMainMenu()
    if (prevIsOpen !== undefined && !isMenuOpen) closeMainMenu()
  }, [isMenuOpen])

  /*------------------------------
  Close Nav on change page
  ------------------------------*/
  useLayoutEffect(() => {
    if (prevPathname !== pathname) {
      setTimeout(() => {
        if (isMenuOpen) closeMenu()
      }, 500)
    }
  }, [pathname])

  /*------------------------------
  Render Main Nav
  ------------------------------*/
  const renderMainNav = useCallback(() => {
    return (
      <nav
        className={`${classes.nav} ${classes.mainNav}`}
        ref={$mainNav}
      >
        <ul>
          {!isLanding && (
            mainNav.map((item, index) => {
              if (!item.child) {
                return (
                  <li
                    className={classes.mainVoices}
                    key={(100 + index).toString()}
                  >
                    <DelayLink
                      className={classNames({
                        [classes.active]: getSlug(item.url) === getSlug(location.pathname),
                      })}
                      to={item.url}
                    >
                      <UnderlinedText
                        text={decodeEntities(item.title)}
                      />
                    </DelayLink>
                    {/* {item.description === 'noDelayLink' ? (
                      <a
                        className={classNames({
                          [classes.active]: getSlug(item.url) === getSlug(location.pathname),
                        })}
                        href={item.url}
                      >
                        <UnderlinedText
                          text={decodeEntities(item.title)}
                        />
                      </a>
                    ) : (
                      <DelayLink
                        className={classNames({
                          [classes.active]: getSlug(item.url) === getSlug(location.pathname),
                        })}
                        to={item.url}
                      >
                        <UnderlinedText
                          text={decodeEntities(item.title)}
                        />
                      </DelayLink>
                    )} */}
                  </li>
                )
              }

              return (
                <li
                  className={classes.services}
                  key={(100 + index).toString()}
                >
                  <h3>
                    {item.title}
                  </h3>
                  <ul>
                    {item.child.map((subheadings, i) => (
                      <li
                        className={classes.subheadings}
                        key={i.toString()}
                      >
                        <DelayLink
                          className={classNames({
                            [classes.active]: getSlug(subheadings.url) === getSlug(location.pathname),
                          })}
                          to={subheadings.url}
                        >
                          <UnderlinedText
                            text={decodeEntities(subheadings.title)}
                            strokeWidth={2}
                            height={10}
                          />
                        </DelayLink>
                      </li>
                    ))}
                  </ul>
                </li>
              )
            }))}
          {(isLanding && mainMenuLanding.length > 0) && (
            mainMenuLanding.map((item, index) => {
              return (
                <li
                  className={classes.mainVoices}
                  key={(200 + index).toString()}
                >
                  <DelayLink
                    to={item.link_main}
                  >
                    <UnderlinedText
                      text={decodeEntities(item.label_main)}
                    />
                  </DelayLink>
                </li>
              )
            }))}
        </ul>
      </nav>
    )
  }, [mainNav, location, isLanding, mainMenuLanding])

  /*------------------------------
  Render Info
  ------------------------------*/
  const renderSocial = useCallback(() => {
    return (
      <div
        className={classes.social}
        ref={$social}
      >
        {strings.linkedin && (
          <DelayLink
            to={strings.linkedin}
          >
            <svg><use xlinkHref="#ico-linkedin" /></svg>
            <p>Linkedin</p>
          </DelayLink>
        )}
      </div>
    )
  }, [strings])

  return (
    <div
      className={classes.root}
      ref={$root}
      role="dialog"
      aria-modal="true"
    >
      <div className={classes.container}>
        <div
          className={classNames({
            [classes.wrapper]: true,
          })}
          ref={$wrapper}
        >
          {renderMainNav()}
          {renderSocial()}
        </div>
      </div>
    </div>
  )
}

export default memo(Nav)
